import {
  beerify,
  debeerify,
  setUserProfileServerError,
  Spinner,
  useEnvironment,
} from "@keycloak/keycloak-ui-shared";
import { Alert, Button, Link, Stack } from "@mui/material";
import LaunchIcon from '@mui/icons-material/Launch';
import { UserProfileFields } from "../../user-profile/UserProfileFields";
import { TFunction } from "i18next";
import { useState } from "react";
import { ErrorOption, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  getSupportedLocales,
  savePersonalInfo,
} from "../../api/methods";
import {
  UserProfileAttributeMetadata,
  UserRepresentation,
} from "../../api/representations";
import { Page } from "../../components/page/Page";
import type { Environment } from "../../environment";
import { TFuncKey, i18n } from "../../i18n";
import { useAccountAlerts } from "../../utils/useAccountAlerts";
import { usePromise } from "../../utils/usePromise";
import {usePersonalInfoContext} from "../../providers/PersonalInfoProvider";
import { AlertVariant } from "../../alerts/Alerts";
import ExpandableSection from "../../components/extendable-section/ExtendableSection";


export const PersonalInfo = () => {
  const { t } = useTranslation();
  const context = useEnvironment<Environment>();
  const personalInfo = usePersonalInfoContext();
  const [supportedLocales, setSupportedLocales] = useState<string[]>([]);
  const form = useForm<UserRepresentation>({ mode: "onChange" });
  const { handleSubmit, reset, setValue, setError } = form;
  const { addAlert } = useAccountAlerts();

  usePromise(
    (signal) => getSupportedLocales({ signal, context }),
    (supportedLocales) => {
      setSupportedLocales(supportedLocales);
      reset(personalInfo);
      Object.entries(personalInfo.attributes || {}).forEach(([k, v]) =>
        setValue(`attributes[${beerify(k)}]`, v),
      );
    },
  );

  const onSubmit = async (user: UserRepresentation) => {
    try {
      const attributes = Object.fromEntries(
        Object.entries(user.attributes || {}).map(([k, v]) => [
          debeerify(k),
          v,
        ]),
      );
      await savePersonalInfo(context, { ...user, attributes });
      const locale = attributes["locale"]?.toString();
      if (locale)
        i18n.changeLanguage(locale, (error) => {
          if (error) {
            console.warn("Error(s) loading locale", locale, error);
          }
        });
      context.keycloak.updateToken();
      if (attributes["public_email"]){
          window.location.reload(); // todo re-render the page instead of refresh
          addAlert(t("inviteSent"));
      } else {
          addAlert(t("accountUpdatedMessage"));
      }

    } catch (error) {
      addAlert(t("accountUpdatedError"), AlertVariant.error);

      setUserProfileServerError(
        { responseData: { errors: error as any } },
        (name: string | number, error: unknown) =>
          setError(name as string, error as ErrorOption),
        ((key: TFuncKey, param?: object) => t(key, param as any)) as TFunction,
      );
    }
  };

  if (!personalInfo?.userProfileMetadata) {
    return <Spinner />;
  }

  const allFieldsReadOnly = () =>
      personalInfo?.userProfileMetadata?.attributes
      ?.map((a: any) => a.readOnly)
      .reduce((p: any, c: any) => p && c, true);

  const {
    updateEmailFeatureEnabled,
    updateEmailActionEnabled,
    isRegistrationEmailAsUsername,
    isEditUserNameAllowed,
  } = context.environment.features;


const reorderedUserProfileMetadata = {
  ...personalInfo?.userProfileMetadata,
  attributes: [
  ...personalInfo?.userProfileMetadata?.attributes.filter((attr: UserProfileAttributeMetadata) => attr.name === "firstName"),
  ...personalInfo?.userProfileMetadata?.attributes.filter((attr: UserProfileAttributeMetadata) => attr.name === "lastName"),
  ...personalInfo?.userProfileMetadata?.attributes.filter((attr: UserProfileAttributeMetadata) => attr.name !== "firstName" && attr.name !== "lastName")
]};

  return (
    <Page title={t("personalInfo")}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <UserProfileFields
          form={form}
          userProfileMetadata={reorderedUserProfileMetadata}
          supportedLocales={supportedLocales}
          currentLocale={context.environment.locale}
          t={
            ((key: unknown, params) =>
              t(key as TFuncKey, params as any)) as TFunction
          }
          renderer={(attribute) =>
            attribute.name === "email" &&
            updateEmailFeatureEnabled &&
            false &&
            (!isRegistrationEmailAsUsername || isEditUserNameAllowed) ? (
              <Button
                id="update-email-btn"
                variant="outlined"
                onClick={() =>
                  context.keycloak.login({ action: "UPDATE_EMAIL" })
                }
                endIcon={<LaunchIcon />}
              >
                {t("updateEmail")}
              </Button>
            ) : undefined
          }
        />
        {!allFieldsReadOnly() && (
          <Stack direction="row" spacing={1}>
            <Button
              data-testid="save"
              type="submit"
              id="save-btn"
            >
              {t("save")}
            </Button>
            <Button
              data-testid="cancel"
              id="cancel-btn"
              variant="outlined"
              onClick={() => reset()}
            >
              {t("cancel")}
            </Button>
          </Stack>
        )}
        {context.environment.features.deleteAccountAllowed && (
          <ExpandableSection
            data-testid="delete-account"
            toggleText={t("deleteAccount")}
          >
            <Alert
              title={t("deleteAccount")}
              severity="error"
            >
              {t("deleteAccountWarning")}{' '}
              <Link
                  component="button"
                  id="delete-account-btn"
                  color="error"
                  onClick={() =>
                    context.keycloak.login({
                      action: "delete_account",
                    })
                  }
                >
                  {t("delete")}
                </Link>
            </Alert>
          </ExpandableSection>
        )}
      </form>
    </Page>
  );
};

export default PersonalInfo;
